import type {
  AdminModerationFlagListResponse,
  AdminModerationReportContextResponse,
  AdminModerationReportListResponse,
  ResolveModerationReportResponse,
} from '@shared/types';
import { MODERATION } from '@shared/constants';
import { api } from './api';

/**
 * Admin DM-moderation API client. Wraps the REST endpoints that back the
 * Moderation page (report triage + auto-filter flags):
 *
 *   - GET  /admin/moderation/reports?status=&page=&limit=   → paginated report queue
 *   - GET  /admin/moderation/reports/:id/context            → surrounding messages
 *   - POST /admin/moderation/reports/:id/resolve            → body `{ status }`,
 *                                                             status ∈ {reviewed, dismissed}
 *   - GET  /admin/moderation/flags?action=&page=&limit=     → auto-filter events
 *
 * The ban action reuses `banUser` from `services/users.ts` — moderation does
 * not roll its own ban path.
 *
 * Thin on purpose: validation / normalisation lives in the components. Keeping
 * it dumb lets the Moderation screen mock via `vi.mock('../../services/moderation')`
 * without wrestling with fetch internals.
 */

export interface ListModerationReportsParams {
  /** Report status filter. Empty / undefined = no filter (all statuses). */
  status?: 'pending' | 'reviewed' | 'dismissed' | '';
  page?: number;
  limit?: number;
}

export interface ListModerationFlagsParams {
  /** Auto-filter action filter. Empty / undefined = no filter (all actions). */
  action?: 'masked' | 'blocked' | '';
  page?: number;
  limit?: number;
}

/**
 * Build a query string, skipping empty / undefined values. Mirrors the helper
 * in `services/users.ts` — duplicated intentionally so each service stays
 * self-contained and can be unit-tested without a shared helper dependency.
 */
function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function listModerationReports(
  params: ListModerationReportsParams = {},
): Promise<AdminModerationReportListResponse> {
  const qs = toQuery({
    status: params.status,
    page: params.page ?? 1,
    limit: params.limit ?? MODERATION.REPORTS_PAGE_SIZE,
  });
  return api<AdminModerationReportListResponse>(`/admin/moderation/reports${qs}`);
}

export async function getModerationReportContext(
  reportId: string,
): Promise<AdminModerationReportContextResponse> {
  return api<AdminModerationReportContextResponse>(
    `/admin/moderation/reports/${encodeURIComponent(reportId)}/context`,
  );
}

export async function resolveModerationReport(
  reportId: string,
  status: 'reviewed' | 'dismissed',
): Promise<ResolveModerationReportResponse> {
  return api<ResolveModerationReportResponse>(
    `/admin/moderation/reports/${encodeURIComponent(reportId)}/resolve`,
    { method: 'POST', body: JSON.stringify({ status }) },
  );
}

export async function listModerationFlags(
  params: ListModerationFlagsParams = {},
): Promise<AdminModerationFlagListResponse> {
  const qs = toQuery({
    action: params.action,
    page: params.page ?? 1,
    limit: params.limit ?? MODERATION.FLAGS_PAGE_SIZE,
  });
  return api<AdminModerationFlagListResponse>(`/admin/moderation/flags${qs}`);
}
